import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useSession } from 'store/session';
import { SegmentedControl } from '@design-system';
import { IDLE_TIMEOUT_OPTIONS } from 'config/constants';

interface SecuritySettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SecuritySettingsModal: React.FC<SecuritySettingsModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const { idleTimeout, setIdleTimeout } = useSession();
    const toast = useToast();

    const handleChange = (value: number) => {
        setIdleTimeout(value);
        toast({
            title: t(translations.SUCCESS.PROFILE_EDITED, { defaultValue: 'Perfil actualizado' }),
            description: t(translations.SECURITY.IDLE_TIMEOUT_UPDATED, { defaultValue: 'Tiempo actualizado' }),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top',
            variant: 'subtle'
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
            <ModalContent
                borderRadius="2xl"
                _dark={{ bg: 'gray.800', backdropFilter: 'blur(16px)' }}
                bg="whiteAlpha.900"
                backdropFilter="blur(16px)"
            >
                <ModalHeader borderBottom="1px solid" borderColor="gray.100" _dark={{ borderColor: 'whiteAlpha.100' }}>
                    {t(translations.SECURITY.TITLE, { defaultValue: 'Seguridad' })}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={6}>
                    <VStack align="stretch" spacing={6}>
                        <VStack align="start" spacing={1} w="full">
                            <Text fontSize="xs" fontWeight="bold" color="purple.500" textTransform="uppercase">
                                {t('SECURITY.PREFERENCE', { defaultValue: 'Preferencia de bloqueo' })}
                            </Text>
                            <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }}>
                                {t(translations.SECURITY.IDLE_TIMEOUT_DESCRIPTION, { defaultValue: 'Elige cuánto tiempo de inactividad debe pasar antes de bloquear la pantalla.' })}
                            </Text>
                        </VStack>

                        <VStack align="stretch" spacing={2} w="full">
                            <SegmentedControl
                                value={idleTimeout}
                                onChange={handleChange}
                                options={IDLE_TIMEOUT_OPTIONS}
                                layoutId="security-timeout-active"
                            />
                            <Text fontSize="10px" color="gray.400" textAlign="left" fontStyle="italic" lineHeight="shorter">
                                {t('security.practical_hint', { defaultValue: 'Ejemplo: Un tiempo corto protege tu sesión al levantarte por un café o entrar a una reunión. Un tiempo mayor te permite trabajar sin interrupciones en tu espacio de confianza.' })}
                            </Text>
                        </VStack>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="ghost" colorScheme="purple">
                        {t(translations.COMMON.ACCEPT, { defaultValue: 'Aceptar' })}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
